<template>
  <div class="embed-container" :class="{ stacked }">
    <!-- Stacked layout: title bar above the map, so nothing covers the route -->
    <EmbedHeader v-if="stacked" :title="hikeName" :url="hikeUrl">
      <EmbedRoutesPanel
        v-if="hasRoutesPanel"
        v-bind="routesPanelProps"
        hike-name=""
        docked
        @update:collapsed="routesPanelCollapsed = $event"
        @trail-selected="onTrailSelected"
        @view-changed="onViewChanged"
      />
      <HiikerBranding :hike-url="hikeUrl" :hike-name="hikeName" docked />
    </EmbedHeader>

    <div id="map" class="map-container">
      <template v-if="!stacked">
        <!-- Routes Panel (for multi-day or when enabled) -->
        <EmbedRoutesPanel
          v-if="hasRoutesPanel"
          v-bind="routesPanelProps"
          @update:collapsed="routesPanelCollapsed = $event"
          @trail-selected="onTrailSelected"
          @view-changed="onViewChanged"
        />

        <!-- Elevation Profile -->
        <EmbedElevationProfile
          v-if="hasElevationProfile"
          v-bind="elevationProfileProps"
          @update:minimized="elevationMinimized = $event"
        />

        <!-- HiiKER Branding -->
        <HiikerBranding :hike-url="hikeUrl" :hike-name="hikeName" />
      </template>

      <!-- Loading overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
      </div>
    </div>

    <!-- Stacked layout: the profile docks below the map -->
    <EmbedElevationProfile
      v-if="stacked && hasElevationProfile"
      v-bind="elevationProfileProps"
      docked
      @update:minimized="elevationMinimized = $event"
    />

    <!-- Highlight Detail Modal -->
    <EmbedHighlightDetailModal ref="highlightModal" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import mapboxgl from 'mapbox-gl';
import graph_client from '../../utils/graphql/graph_client';
import { embedRouteStyleSettings, type EmbedRouteStyleOverrides } from '../../utils/embeds/embedRouteStyle';
import { buildRouteStyleExpressions } from '../../utils/routeStyleSettings';
import type { EmbedTheme } from '../../utils/embeds/embedUrlParams';
import { GetHike, GetHikeEndpointAddresses } from '../../utils/graphql/hikes-graph';
import { LayerRegistry, MapLayerCategory } from '../../utils/layer-registry';
import { ROUTE_STYLES } from '../../utils/constants/route-styles';
import { POIUtility } from '../../utils/models/POI';
import { MapDataCreditControl } from '../../utils/helpers/MapDataCreditControl';
import EmbedRoutesPanel from './components/EmbedRoutesPanel.vue';
import EmbedElevationProfile from './components/EmbedElevationProfile.vue';
import HiikerBranding from './components/HiikerBranding.vue';
import EmbedHighlightDetailModal from './components/EmbedHighlightDetailModal.vue';
import EmbedHeader from './components/EmbedHeader.vue';
import { buildStageMarkerFeatures, sortFeaturesByTrailOrder } from '../hike_map/helpers/stageMarkers';
import { ensureStageMarkerIcons, STAGE_MARKER_ICON_PREFIX } from '../hike_map/helpers/stageMarkerIcon';
import {
  EndpointAddressPair,
  EndpointBadgeMarkers,
  EndpointNames,
  endpointNamesForRoutes,
  pointCoordinates
} from '../../utils/embeds/endpointBadges';

// Construct map style URL dynamically
function getEmbedMapStyleUrl() {
  const localURL = window.location.protocol + "//" + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
  return `${localURL}/mapbox_style_jsons/standard.json`;
}

declare global {
  interface Window {
    hike_id: string;
    hike_name: string;
    hike_url: string;
    is_multi_day: boolean;
    lonlat?: [number, number];
    embedConfig: {
      showHighlights: boolean;
      showRoutesPanel: boolean;
      routesPanelCollapsed: boolean;
      showElevation: boolean;
      elevationMinimized: boolean;
      // Absent on pages rendered before these options existed.
      showStageMarkers?: boolean;
      layout?: 'overlay' | 'stacked';
      scrollZoom?: boolean;
      endpointLabels?: boolean;
      endpointLabelCountry?: boolean;
      theme?: EmbedTheme;
      routeStyles?: EmbedRouteStyleOverrides;
    };
    map: mapboxgl.Map;
    layerRegistry: LayerRegistry;
  }
}

// Held outside reactive data: Vue's proxies break mapbox objects.
let endpointBadgeMarkers: EndpointBadgeMarkers | null = null;
let mapResizeObserver: ResizeObserver | null = null;

export default defineComponent({
  name: 'HikeMapEmbed',
  components: {
    EmbedRoutesPanel,
    EmbedElevationProfile,
    HiikerBranding,
    EmbedHighlightDetailModal,
    EmbedHeader
  },
  data() {
    return {
      loading: true,
      geojson: null as any,
      hikeName: '',
      hikeUrl: '',
      bbox: [] as number[],
      plannedTrails: [] as any[],
      hikeDays: [] as any[],
      hikeHighlights: [] as any[],
      showHighlights: window.embedConfig?.showHighlights ?? true,
      showRoutesPanel: window.embedConfig?.showRoutesPanel ?? window.is_multi_day,
      routesPanelCollapsed: window.embedConfig?.routesPanelCollapsed ?? false,
      showElevation: window.embedConfig?.showElevation ?? true,
      elevationMinimized: window.embedConfig?.elevationMinimized ?? false,
      showStageMarkers: window.embedConfig?.showStageMarkers ?? false,
      stacked: window.embedConfig?.layout === 'stacked',
      scrollZoom: window.embedConfig?.scrollZoom ?? true,
      endpointLabels: window.embedConfig?.endpointLabels ?? false,
      endpointLabelCountry: window.embedConfig?.endpointLabelCountry ?? false,
      routeStyles: window.embedConfig?.routeStyles ?? {},
      // ab=1 only: the hike's and each route's start/end, to name A and B by.
      hikeEndpointAddresses: null as EndpointAddressPair | null,
      routeEndpointAddresses: {} as Record<string, EndpointAddressPair>,
      endpointNames: null as EndpointNames | null,
      activeView: { type: 'full' } as { type: string; dayNumber?: number; trailId?: string | number },
      mapInstance: null as mapboxgl.Map | null
    };
  },
  computed: {
    hasRoutesPanel(): boolean {
      return this.showRoutesPanel && this.plannedTrails?.length > 0;
    },

    hasElevationProfile(): boolean {
      return this.showElevation && !!this.geojson;
    },

    routesPanelProps(): Record<string, unknown> {
      return {
        hikeName: this.hikeName,
        plannedTrails: this.plannedTrails,
        hikeDays: this.hikeDays,
        collapsed: this.routesPanelCollapsed,
        dayBadges: this.showStageMarkers
      };
    },

    elevationProfileProps(): Record<string, unknown> {
      return {
        geojson: this.geojson,
        activeView: this.activeView,
        plannedTrails: this.plannedTrails,
        hikeDays: this.hikeDays,
        minimized: this.elevationMinimized,
        highlights: this.hikeHighlights,
        endpointBadges: this.endpointLabels,
        endpointNames: this.endpointNames
      };
    }
  },
  async mounted() {
    // Set global values
    this.hikeName = window.hike_name || '';
    this.hikeUrl = window.hike_url || '';

    // Fetch data first
    await Promise.all([this.fetchData(), this.endpointLabels ? this.fetchEndpointAddresses() : null]);

    // Initialize map
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w';

    let mapOptions: mapboxgl.MapboxOptions = {
      container: 'map',
      style: getEmbedMapStyleUrl(),
      attributionControl: false, // We'll add our own branding
      // Off when the embedder asks, so scrolling their page past the map
      // doesn't zoom it instead. The +/- buttons still work.
      scrollZoom: this.scrollZoom
    };

    if (this.bbox && this.bbox.length === 4) {
      mapOptions.bounds = [
        [this.bbox[0], this.bbox[1]],
        [this.bbox[2], this.bbox[3]]
      ] as mapboxgl.LngLatBoundsLike;
      mapOptions.fitBoundsOptions = { padding: 60 };
    } else if (window.lonlat) {
      mapOptions.center = window.lonlat;
      mapOptions.zoom = 12;
    } else {
      mapOptions.center = [0, 0];
      mapOptions.zoom = 2;
    }

    window.map = new mapboxgl.Map(mapOptions);
    this.mapInstance = window.map;
    window.layerRegistry = new LayerRegistry(window.map);
    if (this.endpointLabels) {
      endpointBadgeMarkers = new EndpointBadgeMarkers(window.map, (element) => new mapboxgl.Marker({ element }));
    }

    // Add minimal controls
    window.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    // The embed hides the Mapbox attribution strip and wordmark by class, deliberately — but a
    // provider whose licence asks for a visible credit still needs one. This carries its own class,
    // so those rules leave it alone.
    window.map.addControl(new MapDataCreditControl(), 'bottom-right');

    window.map.on('load', () => {
      this.addToMap();
      this.loading = false;
    });

    window.map.on('style.load', () => {
      // Wait for style to fully load including sprites
      if (window.map.isStyleLoaded()) {
        this.addToMap();
        if (this.showHighlights) {
          this.addHighlightsToMap();
        }
      }
    });

    // Handle resize
    window.addEventListener('resize', this.handleResize);

    // In the stacked layout the map also changes height when the profile
    // below it is minimized or expanded, which a window resize doesn't catch.
    if (typeof ResizeObserver !== 'undefined') {
      mapResizeObserver = new ResizeObserver(() => this.handleResize());
      mapResizeObserver.observe(window.map.getContainer());
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    mapResizeObserver?.disconnect();
    mapResizeObserver = null;
    endpointBadgeMarkers?.clear();
    endpointBadgeMarkers = null;
    if (window.map) {
      this.removeHighlightsFromMap();
      this.removeExistingLayers();
      window.map.remove();
    }
  },
  methods: {
    handleResize() {
      if (window.map) {
        window.map.resize();
      }
    },

    async fetchData() {
      try {
        const response = await graph_client.request(GetHike, { id: `${window.hike_id}` }) as any;
        const hike = response.hike;
        const geojson = JSON.parse(hike.geojsonMultilinestringsWithStartEndPointsString);

        this.geojson = geojson;
        this.hikeName = hike.name || window.hike_name;
        this.bbox = geojson.bbox;
        this.plannedTrails = hike.hikesPlannedTrails || hike.ptv2PlannedTrails || [];
        this.hikeDays = hike.hikeDays || [];
        this.hikeHighlights = hike.hikeHighlights || [];
      } catch (error) {
        console.error('Error fetching hike data:', error);
      }
    },

    // A failure here only costs the place names; A and B still show.
    async fetchEndpointAddresses() {
      try {
        const response = await graph_client.request(GetHikeEndpointAddresses, { id: `${window.hike_id}` }) as any;
        const hike = response.hike;
        if (!hike) return;
        this.hikeEndpointAddresses = { start: hike.startAddress, end: hike.endAddress };
        const routes: Record<string, EndpointAddressPair> = {};
        (hike.hikesPlannedTrails || []).forEach((link: any) => {
          const trail = link?.ptv2PlannedTrail;
          if (trail?.id != null) routes[String(trail.id)] = { start: trail.startAddress, end: trail.endAddress };
        });
        this.routeEndpointAddresses = routes;
      } catch (error) {
        console.error('Error fetching hike endpoint addresses:', error);
      }
    },

    addToMap() {
      if (!this.geojson) return;

      this.removeExistingLayers();

      let filteredFeatures = this.geojson.features;

      if (this.activeView.type === 'day') {
        filteredFeatures = this.geojson.features.filter(
          (f: any) => f.properties && f.properties.hikeDayNumber === this.activeView.dayNumber
        );
      } else if (this.activeView.type === 'route') {
        filteredFeatures = this.geojson.features.filter((f: any) => {
          const featureTrailId = f.properties?.ptv2PlannedTrailId;
          const targetTrailId = this.activeView.trailId;
          return featureTrailId != null && String(featureTrailId) === String(targetTrailId);
        });
      }

      const multiLineStrings = filteredFeatures.filter((f: any) => f.geometry.type === 'MultiLineString');
      const { startMarkers, endMarkers } = this.getStartEndMarkers(filteredFeatures);

      // Marker size expression
      const markerIconSizeExpression: mapboxgl.Expression = [
        'interpolate',
        ['linear'],
        ['zoom'],
        0, 0.1,
        9, 0.2,
        12, 0.3,
        22, 0.5
      ];

      // Add route lines
      if (multiLineStrings.length > 0) {
        // The owner's colour, thickness and opacity per route type travel in the embed URL, and are
        // painted by the same expressions as the map, so the embed matches their map.
        const styleExprs = buildRouteStyleExpressions(embedRouteStyleSettings(this.routeStyles));
        const colorExpression = styleExprs.colorExpr as mapboxgl.Expression;
        const borderColorExpression = styleExprs.borderColorExpr as mapboxgl.Expression;

        const dashArrayExpression: mapboxgl.Expression = [
          'case',
          ['==', ['get', 'routeStyle'], 'transport'],
          ['literal', ROUTE_STYLES.transport.dasharray || [1, 0]],
          ['literal', [1, 0]]
        ];

        // Border layer
        window.layerRegistry.addLayer(
          {
            id: 'hike-lines-border',
            type: 'line',
            source: 'hike-lines',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': borderColorExpression,
              'line-width': styleExprs.activeBorderWidthExpr as mapboxgl.Expression,
              'line-opacity': styleExprs.activeBorderOpacityExpr as mapboxgl.Expression,
              'line-dasharray': dashArrayExpression
            }
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-lines',
              spec: {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: multiLineStrings }
              }
            },
            z: 9
          }
        );

        // Main line layer
        window.layerRegistry.addLayer(
          {
            id: 'hike-lines',
            type: 'line',
            source: 'hike-lines',
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': colorExpression,
              'line-width': styleExprs.activeLineWidthExpr as mapboxgl.Expression,
              'line-opacity': styleExprs.activeLineOpacityExpr as mapboxgl.Expression,
              'line-dasharray': dashArrayExpression
            }
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-lines',
              spec: {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: multiLineStrings }
              }
            },
            z: 10
          }
        );
      }

      // A/B badges replace the start/end pins when the embedder asks for them.
      if (endpointBadgeMarkers) {
        this.endpointNames = endpointNamesForRoutes(
          startMarkers[0]?.properties?.ptv2PlannedTrailId,
          endMarkers[0]?.properties?.ptv2PlannedTrailId,
          this.routeEndpointAddresses,
          // The hike's own start/end only describes the whole hike.
          this.activeView.type === 'full' ? this.hikeEndpointAddresses : null,
          { withCountry: this.endpointLabelCountry }
        );
        endpointBadgeMarkers.set(
          pointCoordinates(startMarkers[0]),
          pointCoordinates(endMarkers[0]),
          this.endpointNames
        );
      }

      // Add markers
      if (!endpointBadgeMarkers && endMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'hike-markers-end',
            type: 'symbol',
            source: 'hike-markers-end',
            layout: {
              'icon-image': 'hike-control-point-end',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom',
              'icon-allow-overlap': true
            }
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-markers-end',
              spec: {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: endMarkers }
              }
            },
            z: 20
          }
        );
      }

      if (!endpointBadgeMarkers && startMarkers.length > 0) {
        window.layerRegistry.addLayer(
          {
            id: 'hike-markers-start',
            type: 'symbol',
            source: 'hike-markers-start',
            layout: {
              'icon-image': 'hike-control-point-start',
              'icon-size': markerIconSizeExpression,
              'icon-anchor': 'bottom',
              'icon-allow-overlap': true
            }
          },
          MapLayerCategory.Annotations,
          {
            source: {
              id: 'hike-markers-start',
              spec: {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: startMarkers }
              }
            },
            z: 25
          }
        );
      }

      // The hike map's numbered day badges, where each day after the first begins. Empty for a
      // single-day hike and for a day or route view, which only hold one day's routes.
      const stageMarkers = this.showStageMarkers
        ? buildStageMarkerFeatures(sortFeaturesByTrailOrder(filteredFeatures, this.plannedTrails))
        : [];
      if (stageMarkers.length > 0) {
        ensureStageMarkerIcons(window.map, stageMarkers.map((f: any) => f.properties.stageNumber));
        // Top, as on the hike map, so the badges sit above the highlight markers.
        window.layerRegistry.addLayer(
          {
            id: 'hike-stage-markers',
            type: 'symbol',
            source: 'hike-stage-markers',
            layout: {
              'icon-image': ['concat', STAGE_MARKER_ICON_PREFIX, ['to-string', ['get', 'stageNumber']]],
              'icon-size': 1,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'icon-anchor': 'center',
              'icon-pitch-alignment': 'map',
              'symbol-z-order': 'source'
            }
          },
          MapLayerCategory.Top,
          {
            source: {
              id: 'hike-stage-markers',
              spec: {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: stageMarkers }
              }
            },
            z: 20
          }
        );
      }

      // Fit bounds based on view
      if (this.activeView.type !== 'full') {
        const bounds = this.computeBoundsFromFeatures(filteredFeatures);
        if (bounds) {
          window.map.fitBounds(bounds, { padding: 60 });
        }
      } else if (this.bbox && this.bbox.length === 4) {
        window.map.fitBounds(
          [[this.bbox[0], this.bbox[1]], [this.bbox[2], this.bbox[3]]],
          { padding: 60 }
        );
      }

      // Add highlights if enabled
      if (this.showHighlights) {
        this.addHighlightsToMap();
      }
    },

    getStartEndMarkers(filteredFeatures: any[]) {
      const result = { startMarkers: [] as any[], endMarkers: [] as any[] };
      if (!filteredFeatures || filteredFeatures.length === 0) return result;

      const markerLookup = this.buildMarkerLookup(filteredFeatures);

      const isPrimaryRoute = (routeStyle: string) => !routeStyle || routeStyle === 'route';

      if (this.activeView.type === 'route') {
        const routeId = this.activeView.trailId;
        const startMarker = markerLookup.start[String(routeId)];
        const endMarker = markerLookup.end[String(routeId)];
        if (startMarker) result.startMarkers.push(startMarker);
        if (endMarker) result.endMarkers.push(endMarker);
        return result;
      }

      const routeLineFeatures = filteredFeatures.filter(
        (f) => f.geometry?.type === 'MultiLineString' && isPrimaryRoute(f.properties?.routeStyle)
      );

      if (routeLineFeatures.length === 0) return result;

      const firstRoute = routeLineFeatures[0];
      const lastRoute = routeLineFeatures[routeLineFeatures.length - 1];

      const firstRouteId = firstRoute?.properties?.ptv2PlannedTrailId;
      const lastRouteId = lastRoute?.properties?.ptv2PlannedTrailId;

      if (firstRouteId) {
        const marker = markerLookup.start[String(firstRouteId)];
        if (marker) result.startMarkers.push(marker);
      }

      if (lastRouteId) {
        const marker = markerLookup.end[String(lastRouteId)];
        if (marker) result.endMarkers.push(marker);
      }

      return result;
    },

    buildMarkerLookup(features: any[]) {
      const lookup: { start: Record<string, any>; end: Record<string, any> } = {
        start: {},
        end: {}
      };

      features.forEach((feature) => {
        if (feature.geometry?.type !== 'Point') return;
        const props = feature.properties || {};
        const trailId = props.ptv2PlannedTrailId;
        if (trailId == null || !props.markerType) return;

        const key = String(trailId);
        if (props.markerType === 'start') {
          lookup.start[key] = feature;
        } else if (props.markerType === 'end') {
          lookup.end[key] = feature;
        }
      });

      return lookup;
    },

    computeBoundsFromFeatures(features: any[]): mapboxgl.LngLatBounds | null {
      const bounds = new mapboxgl.LngLatBounds();
      let hasBounds = false;

      features.forEach((feature) => {
        if (!feature?.geometry) return;

        if (feature.geometry.type === 'MultiLineString') {
          feature.geometry.coordinates.forEach((lineString: number[][]) => {
            lineString.forEach((coord: number[]) => {
              if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                bounds.extend([coord[0], coord[1]]);
                hasBounds = true;
              }
            });
          });
        } else if (feature.geometry.type === 'Point') {
          const coord = feature.geometry.coordinates;
          if (!isNaN(coord[0]) && !isNaN(coord[1])) {
            bounds.extend([coord[0], coord[1]]);
            hasBounds = true;
          }
        }
      });

      return hasBounds ? bounds : null;
    },

    removeExistingLayers() {
      const layersToRemove = [
        'hike-lines', 'hike-lines-border', 'hike-markers-start', 'hike-markers-end', 'hike-stage-markers'
      ];
      const sourcesToRemove = ['hike-lines', 'hike-markers-start', 'hike-markers-end', 'hike-stage-markers'];

      layersToRemove.forEach((layerId) => {
        if (window.map.getLayer(layerId)) {
          window.map.removeLayer(layerId);
        }
      });

      sourcesToRemove.forEach((sourceId) => {
        if (window.map.getSource(sourceId)) {
          window.map.removeSource(sourceId);
        }
      });
    },

    getFilteredHighlights() {
      if (!this.hikeHighlights?.length) {
        return [];
      }

      // If viewing full route, show all highlights
      if (this.activeView.type === 'full') {
        return this.hikeHighlights;
      }

      // If viewing a specific day, filter by day number
      if (this.activeView.type === 'day') {
        return this.hikeHighlights.filter(
          highlight => highlight.dayNumber === this.activeView.dayNumber
        );
      }

      // If viewing a specific route, find which day that route belongs to
      if (this.activeView.type === 'route') {
        // Find the trail in plannedTrails
        const trail = this.plannedTrails.find((t: any) => {
          const trailId = t.ptv2PlannedTrail?.id || t.id;
          return String(trailId) === String(this.activeView.trailId);
        });

        if (trail && trail.day != null) {
          // Filter highlights by the day this route belongs to
          return this.hikeHighlights.filter(
            highlight => highlight.dayNumber === trail.day
          );
        }
      }

      // Default: show all highlights
      return this.hikeHighlights;
    },

    addHighlightsToMap() {
      if (!this.showHighlights || !this.hikeHighlights?.length || !window.map || !window.layerRegistry) {
        return;
      }

      // Remove existing highlights first
      this.removeHighlightsFromMap();

      // Filter highlights based on active view
      const filteredHighlights = this.getFilteredHighlights();

      filteredHighlights.forEach((highlight) => {
        const id = `highlight-${highlight.id || highlight.clientId}`;

        let iconName = 'highlight_icon_custom';
        try {
          const poiUtil = POIUtility.fromString(highlight.type);
          iconName = poiUtil.hikeHighlightIcon();
        } catch (e) {
          // Use default icon
        }

        // Check if icon exists in the loaded style
        const hasIcon = window.map.hasImage(iconName);

        const source = {
          type: 'geojson' as const,
          data: {
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: highlight.coordinates },
            properties: {
              id: highlight.id,
              type: highlight.type,
              title: highlight.title,
              description: highlight.description
            }
          }
        };

        // If icon doesn't exist, use a circle marker as fallback
        if (!hasIcon) {
          const layer: mapboxgl.AnyLayer = {
            id,
            type: 'circle',
            source: id,
            paint: {
              'circle-radius': 8,
              'circle-color': '#FFDF46',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#fff'
            }
          };

          window.layerRegistry.addLayer(layer, MapLayerCategory.Annotations, {
            source: { id, spec: source },
            z: 30,
            replace: true
          });

          // Add label layer for circle markers
          const labelId = `${id}-label`;
          const labelLayer: mapboxgl.AnyLayer = {
            id: labelId,
            type: 'symbol',
            source: id,
            layout: {
              'text-field': ['get', 'title'],
              'text-font': ['Roboto Regular', 'Arial Unicode MS Regular'],
              'text-size': 10,
              'text-offset': [0, 1.5],
              'text-anchor': 'top',
              'text-optional': true
            },
            paint: {
              'text-color': '#333',
              'text-halo-color': '#fff',
              'text-halo-width': 1.5
            }
          };

          window.layerRegistry.addLayer(labelLayer, MapLayerCategory.Annotations, {
            source: { id, spec: source },
            z: 31,
            replace: true
          });

          // Add click handler for circle markers
          window.map.on('click', id, () => {
            this.showHighlightDetail(highlight);
          });

          // Add click handler for label
          window.map.on('click', labelId, () => {
            this.showHighlightDetail(highlight);
          });

          // Change cursor on hover
          window.map.on('mouseenter', id, () => {
            window.map.getCanvas().style.cursor = 'pointer';
          });
          window.map.on('mouseleave', id, () => {
            window.map.getCanvas().style.cursor = '';
          });
          window.map.on('mouseenter', labelId, () => {
            window.map.getCanvas().style.cursor = 'pointer';
          });
          window.map.on('mouseleave', labelId, () => {
            window.map.getCanvas().style.cursor = '';
          });
        } else {
          // Use icon if available
          const layer: mapboxgl.AnyLayer = {
            id,
            type: 'symbol',
            source: id,
            layout: {
              'icon-image': iconName,
              'icon-size': 0.25,
              'icon-allow-overlap': true,
              'text-field': ['get', 'title'],
              'text-font': ['Roboto Regular', 'Arial Unicode MS Regular'],
              'text-size': 10,
              'text-offset': [0, 1.2],
              'text-anchor': 'top',
              'text-optional': true
            },
            paint: {
              'text-color': '#333',
              'text-halo-color': '#fff',
              'text-halo-width': 1.5
            }
          };

          window.layerRegistry.addLayer(layer, MapLayerCategory.Annotations, {
            source: { id, spec: source },
            z: 30,
            replace: true
          });

          // Add click handler for icon markers
          window.map.on('click', id, () => {
            this.showHighlightDetail(highlight);
          });

          // Change cursor on hover
          window.map.on('mouseenter', id, () => {
            window.map.getCanvas().style.cursor = 'pointer';
          });
          window.map.on('mouseleave', id, () => {
            window.map.getCanvas().style.cursor = '';
          });
        }
      });
    },

    removeHighlightsFromMap() {
      if (!this.hikeHighlights?.length || !window.map) return;

      this.hikeHighlights.forEach((highlight) => {
        const id = `highlight-${highlight.id || highlight.clientId}`;
        const labelId = `${id}-label`;

        // Remove event listeners
        window.map.off('click', id);
        window.map.off('click', labelId);
        window.map.off('mouseenter', id);
        window.map.off('mouseleave', id);
        window.map.off('mouseenter', labelId);
        window.map.off('mouseleave', labelId);

        if (window.map.getLayer(labelId)) window.map.removeLayer(labelId);
        if (window.map.getLayer(id)) window.map.removeLayer(id);
        if (window.map.getSource(id)) window.map.removeSource(id);
      });
    },

    onTrailSelected(trail: any, index: number) {
      // Pan to selected trail
      if (this.geojson?.features) {
        const trailId = trail.id;
        const filteredFeatures = this.geojson.features.filter(
          (f: any) => f.properties && f.properties.ptv2PlannedTrailId === trailId
        );

        if (filteredFeatures.length > 0) {
          const bounds = this.computeBoundsFromFeatures(filteredFeatures);
          if (bounds) {
            window.map.fitBounds(bounds, { padding: 60 });
          }
        }
      }
    },

    onViewChanged(viewData: { type: string; dayNumber?: number; trailId?: string | number }) {
      this.activeView = viewData;
      this.addToMap();
    },

    showHighlightDetail(highlight: any) {
      const modalRef = this.$refs.highlightModal as any;
      if (modalRef && modalRef.showHighlight) {
        modalRef.showHighlight(highlight);
      }
    }
  }
});
</script>

<style>
/* Import Mapbox GL CSS */
@import 'mapbox-gl/dist/mapbox-gl.css';
</style>

<style scoped>
.embed-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--embed-bg, #1a1b21);
}

.map-container {
  width: 100%;
  height: 100%;
  position: relative;
}

/* Stacked layout: title bar, map, profile — top to bottom, none overlapping. */
.embed-container.stacked {
  display: flex;
  flex-direction: column;
}

.embed-container.stacked .map-container {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--embed-bg-veil, rgba(26, 27, 33, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 223, 70, 0.2);
  border-top-color: #FFDF46;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Override Mapbox attribution for embeds */
:deep(.mapboxgl-ctrl-attrib) {
  display: none !important;
}

:deep(.mapboxgl-ctrl-logo) {
  display: none !important;
}
</style>
